import React, { useCallback, useEffect, useRef, useState } from "react"
import { Mic, Square, RefreshCcw } from "lucide-react"
import { RecordButton } from "@/components/ui/media-button"
import { useAudioRecorder } from "@/app/recsys/hooks/useAudioRecorder"
import { forgetFailedStart, shouldAutoStart } from "./autoStartRule"
import { formatMsToMmSs } from "@/app/recsys/utils"
import { cn } from "@/lib/utils"

export type DemoScriptControl = {
    /** Tour is on the start-recording step — intercept the next start click. */
    armed: boolean
    /** Demo mp3 is playing — show stop UI and intercept stop. */
    active: boolean
    /** Playback elapsed seconds for the timer. */
    elapsedSec: number
}

interface AudioRecorderProps {
    onRecordingStart?: () => void
    onChunk?: (blob: Blob, index: number) => void
    onRecordingComplete?: (result: { blob: Blob; durationSec: number; totalChunks: number }) => void
    onRecordingError?: () => void
    onUploadSuccess?: () => void
    /** Ключ запуска: новая непустая строка = новая встреча, которую надо
     * начать. Булев флаг здесь не годится - он остаётся поднятым после первой
     * встречи, и вторая за ту же сессию вкладки не стартовала бы. */
    autoStart?: string | null
    className?: string
    /** When set, start/stop clicks drive the demo script instead of the mic. */
    demoScript?: DemoScriptControl | null
    onDemoScriptToggle?: () => void
    registerTourTarget?: (id: string, el: HTMLElement | null) => void
}

const WAVE_BAR_WIDTH = 2.5
const WAVE_BAR_GAP = 2.5
const WAVE_BAR_COUNT_FALLBACK = 40
const IDLE_WAVE_HEIGHTS = [6, 10, 14, 10, 6, 4, 8, 12, 8, 4] as const

function waveBarCountForWidth(width: number): number {
    return Math.max(
        8,
        Math.floor((width + WAVE_BAR_GAP) / (WAVE_BAR_WIDTH + WAVE_BAR_GAP)),
    )
}

function waveBarMaxHeight(index: number, count: number): number {
    const center = count / 2
    const dist = center > 0 ? Math.abs(index - center) / center : 0
    const base = 26 * (1 - dist * 0.5)
    const jitter = ((index * 17 + 5) % 13) - 6
    return Math.max(4, base + jitter)
}

function RecorderIconBadge({
    tone,
    children,
}: {
    tone: "idle" | "recording" | "error"
    children: React.ReactNode
}) {
    return (
        <div
            className={cn(
                "flex size-8 shrink-0 items-center justify-center rounded-full border",
                tone === "idle" && "border-neutral-text-900/10 bg-neutral-text-900/10",
                tone === "recording" && "border-state-negative/30 bg-state-negative/12",
                tone === "error" && "border-state-negative/30 bg-state-negative-light",
            )}
        >
            {children}
        </div>
    )
}

function IdleWaveform() {
    return (
        <div className="absolute right-5 z-10 flex items-center" style={{ gap: 2.5 }}>
            {IDLE_WAVE_HEIGHTS.map((height, index) => (
                <span
                    key={index}
                    className="rounded-full bg-neutral-text-900/15"
                    style={{ width: 2.5, height }}
                />
            ))}
        </div>
    )
}

function LiveWaveform() {
    const containerRef = useRef<HTMLDivElement>(null)
    const [barCount, setBarCount] = useState(WAVE_BAR_COUNT_FALLBACK)

    useEffect(() => {
        const el = containerRef.current
        if (!el) return

        const update = (width: number) => {
            const next = waveBarCountForWidth(width)
            setBarCount((prev) => (prev === next ? prev : next))
        }

        update(el.clientWidth)
        if (typeof ResizeObserver === "undefined") return

        const observer = new ResizeObserver((entries) => {
            update(entries[0]?.contentRect.width ?? 0)
        })
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    return (
        <div
            ref={containerRef}
            className="flex h-full min-w-0 flex-1 items-center overflow-hidden px-2"
            style={{ gap: WAVE_BAR_GAP }}
            aria-hidden
            data-testid="live-waveform"
        >
            {Array.from({ length: barCount }, (_, index) => (
                <span
                    key={index}
                    className="record-wave-bar shrink-0 rounded-full bg-neutral-text-900/55"
                    style={
                        {
                            width: WAVE_BAR_WIDTH,
                            height: 3,
                            "--record-wave-bar-max": `${waveBarMaxHeight(index, barCount)}px`,
                            animation: `recsys-wave-bar ${0.25 + (index % 9) * 0.06}s ease-in-out ${((index * 13) % 19) * 0.035}s infinite alternate`,
                        } as React.CSSProperties
                    }
                />
            ))}
        </div>
    )
}

export const AudioRecorder: React.FC<AudioRecorderProps> = ({
    onRecordingStart,
    onChunk,
    onRecordingComplete,
    onRecordingError,
    onUploadSuccess,
    autoStart,
    demoScript,
    onDemoScriptToggle,
    registerTourTarget,
}) => {
    const handleStop = useCallback(
        ({ blob, durationSec, totalChunks }: { blob: Blob; durationSec: number; totalChunks: number }) => {
            onRecordingComplete?.({ blob, durationSec, totalChunks })
            onUploadSuccess?.()
        },
        [onRecordingComplete, onUploadSuccess],
    )

    const { state, time, error, startRecording, stopRecording, resetError } = useAudioRecorder({
        onChunk,
        onStop: handleStop,
        onError: onRecordingError,
    })

    const demoActive = Boolean(demoScript?.active)
    const demoArmed = Boolean(demoScript?.armed)
    const interceptDemo = demoActive || demoArmed

    const isRecording = demoActive || state === "recording"
    const isError = !demoActive && state === "error"
    const displayTimeSec = demoActive ? (demoScript?.elapsedSec ?? 0) : time

    const startGuardRef = useRef(false)
    useEffect(() => {
        if (state === "recording" || state === "error" || demoActive) startGuardRef.current = false
    }, [state, demoActive])

    const handleClick = () => {
        if (interceptDemo) {
            onDemoScriptToggle?.()
            return
        }
        if (isError) {
            resetError()
            return
        }
        if (state === "recording") {
            stopRecording()
            return
        }
        // Synchronous guard: a double click must not spawn two copilot
        // sessions / two recorders before React re-renders the state.
        if (startGuardRef.current) return
        startGuardRef.current = true
        onRecordingStart?.()
        startRecording()
    }

    const autoStartedRef = useRef<string | null>(null)
    useEffect(() => {
        if (forgetFailedStart(state)) autoStartedRef.current = null
        // Never auto-start the real mic while the demo script owns the button.
        if (interceptDemo) return
        if (!shouldAutoStart(autoStart, autoStartedRef.current, state)) return
        autoStartedRef.current = autoStart ?? null
        handleClick()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoStart, state, interceptDemo])

    const recordButtonRef = useCallback(
        (el: HTMLButtonElement | null) => {
            registerTourTarget?.("start-recording", el)
        },
        [registerTourTarget],
    )

    const tone = isError ? "error" : isRecording ? "recording" : "idle"

    return (
        <RecordButton
            ref={recordButtonRef}
            onClick={handleClick}
            aria-invalid={isError}
            aria-pressed={isRecording}
            aria-label={isError ? "Повторить попытку" : isRecording ? "Остановить запись" : "Начать запись"}
            data-tour-target="start-recording"
        >
            <div className="relative z-10 flex shrink-0 items-center gap-3 pl-5">
                <RecorderIconBadge tone={tone}>
                    {isError ? (
                        <RefreshCcw strokeWidth={2.5} className="size-4 text-state-negative" />
                    ) : isRecording ? (
                        <Square className="size-3 fill-state-negative text-state-negative" />
                    ) : (
                        <Mic strokeWidth={2.5} className="record-mic-pulse size-4 text-neutral-text-500" />
                    )}
                </RecorderIconBadge>

                {isError ? (
                    <div className="flex flex-col items-start">
                        <span className="text-body2 font-medium text-state-negative">Повторить попытку</span>
                        {error && (
                            <span className="text-caption font-medium text-state-negative/70">{error}</span>
                        )}
                    </div>
                ) : !isRecording ? (
                    <span className="text-body2 font-medium text-neutral-text-500">Начать запись</span>
                ) : null}
            </div>

            {isRecording && <LiveWaveform />}

            {isRecording && (
                <div className="relative z-10 flex shrink-0 items-center gap-2 pr-5">
                    <span className="record-blink-dot size-2 shrink-0 rounded-full bg-state-negative" />
                    <span className="font-mono text-body2 tracking-wide text-neutral-text-900/45">
                        {formatMsToMmSs(displayTimeSec * 1000)}
                    </span>
                </div>
            )}

            {!isRecording && !isError && <IdleWaveform />}
        </RecordButton>
    )
}
